import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { expenseApi } from '../../services/api'
import ReceiptViewer from '../../components/common/ReceiptViewer'
import { useReceiptViewer } from '../../hooks/useReceiptViewer'
import PlateField from '../../components/common/PlateField'
import ReceiptPicker from '../../components/common/ReceiptPicker'
import CarWashField from '../../components/common/CarWashField'
import { carWashProblem, isForeign, amountFields, amountProblem } from '../../utils/expenseMoney'
import AmountField, { ForeignAmountNote } from '../../components/common/AmountField'

function currentMonth() {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function money(n) {
    return (Number(n) || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' })
}

function monthLabel(month) {
    const [y, m] = month.split('-').map(Number)
    return new Date(y, m - 1, 1).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' })
}

/**
 * Last day of the month, so the report range covers it fully.
 *
 * Formatted from the local date parts rather than via toISOString(), which
 * converts to UTC and would hand back the previous day in any timezone east of
 * Greenwich - silently dropping the last day's receipts from the report.
 */
function monthEnd(month) {
    const [y, m] = month.split('-').map(Number)
    const last = new Date(y, m, 0).getDate()
    return `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`
}

export default function ExpenseAdmin() {
    const [month, setMonth] = useState(currentMonth())
    const [employees, setEmployees] = useState([])
    const [selected, setSelected] = useState(null)       // { employeeUser, cardNumber, fullName }
    const [expenses, setExpenses] = useState([])
    const [categories, setCategories] = useState([])
    const [taxRate, setTaxRate] = useState(0.13)
    const [currencies, setCurrencies] = useState(['USD'])

    const [loadingEmployees, setLoadingEmployees] = useState(true)
    const [loadingRows, setLoadingRows] = useState(false)
    const [generating, setGenerating] = useState(false)

    const [editing, setEditing] = useState(null)         // uuid being edited
    const [draft, setDraft] = useState({})
    const [draftReceipt, setDraftReceipt] = useState(null)   // a replacement image, sent once the line saves
    const { receipt: receiptView, loadingId: openingReceipt, open: openReceipt, close: closeReceipt } = useReceiptViewer()
    const [adding, setAdding] = useState(false)          // a receipt that never got entered
    const [newLine, setNewLine] = useState(null)
    const [newReceipt, setNewReceipt] = useState(null)

    useEffect(() => {
        expenseApi.getCategories(true)
            .then(r => {
                if (!r.success) return
                setCategories(r.categories || [])
                if (r.taxRate > 0) setTaxRate(r.taxRate)
                if (r.currencies?.length) setCurrencies(r.currencies)
            })
            .catch(() => toast.error('Could not load categories'))
    }, [])

    useEffect(() => {
        loadEmployees()
        setSelected(null)
        setExpenses([])
    }, [month])

    useEffect(() => {
        if (selected) loadRows()
    }, [selected])

    async function loadEmployees() {
        setLoadingEmployees(true)
        try {
            const response = await expenseApi.getEmployees(month)
            setEmployees(response.success ? response.employees || [] : [])
        } catch {
            toast.error('Could not load employees')
            setEmployees([])
        }
        setLoadingEmployees(false)
    }

    async function loadRows() {
        setLoadingRows(true)
        try {
            const response = await expenseApi.getForEmployee(selected.employeeUser, month, selected.cardNumber)
            setExpenses(response.success ? response.expenses || [] : [])
        } catch {
            toast.error('Could not load expenses')
            setExpenses([])
        }
        setLoadingRows(false)
    }

    const totals = useMemo(() => expenses.reduce((acc, e) => ({
        preTax: acc.preTax + (Number(e.preTax) || 0),
        tax: acc.tax + (Number(e.tax) || 0),
        tip: acc.tip + (Number(e.tipAmount) || 0),
        paid: acc.paid + (Number(e.amountPaid) || 0),
    }), { preTax: 0, tax: 0, tip: 0, paid: 0 }), [expenses])

    /**
     * Whether a tip may be entered against a category.
     *
     * The PDF hides the tip column on a month with no tips, but this screen always
     * shows it — it is where a missed tip gets corrected, and a column that only
     * appears once the data is already right would be no use for that.
     */
    const promptsTip = id =>
        categories.find(c => String(c.id) === String(id))?.promptsTip === true

    /** Fuel-type categories carry a vehicle plate where the others carry a job. */
    const usesPlate = id =>
        categories.find(c => String(c.id) === String(id))?.usesPlate === true

    /**
     * The plate to send for a line, or null if the line is not usable.
     *
     * Required wherever it replaces the job number: a fuel receipt with no truck
     * on it cannot be costed to anything.
     */
    function plateFor(line) {
        if (!usesPlate(line.categoryId)) return ''

        const plate = String(line.plateNumber ?? '').trim()
        if (!plate) {
            toast.error('Plate number is required for this category')
            return null
        }
        return plate
    }

    /**
     * The tip to send for a line, or null if it is not usable — in which case the
     * reason has already been shown and the caller should stop.
     *
     * Always returns a number rather than leaving the field out, so recategorising
     * a meal as fuel clears the tip that no longer applies instead of stranding it.
     */
    function tipFor(line, amount) {
        // A foreign receipt has no tax split, so a tip on it would mean nothing.
        if (!promptsTip(line.categoryId) || isForeign(line)) return 0

        const raw = String(line.tipAmount ?? '').trim()
        if (!raw) return 0

        const tip = Number(raw)
        if (!Number.isFinite(tip) || tip < 0) {
            toast.error('Enter a valid tip, or leave it blank')
            return null
        }
        if (tip >= amount) {
            toast.error('The tip must be less than the amount paid')
            return null
        }
        return tip
    }

    /** Where a category files its car wash, or null when it offers none. */
    const washTarget = id => {
        const targetId = categories.find(c => String(c.id) === String(id))?.carWashCategoryId
        return targetId == null ? null : categories.find(c => c.id === targetId)?.name || 'its own category'
    }

    /**
     * The car wash to send for a line, or null if it is not usable. Always a
     * number, like the tip, so recategorising away from fuel clears it.
     */
    function carWashFor(line, amount, tip) {
        if (washTarget(line.categoryId) == null || !line.carWash || isForeign(line)) return 0

        const problem = carWashProblem({ ...line, amountPaid: amount, tipAmount: tip, taxRate })
        if (problem) {
            toast.error(problem)
            return null
        }
        return Number(line.carWashAmount)
    }

    function startEdit(expense) {
        setEditing(expense.uuid)
        setDraftReceipt(null)
        setDraft({
            cardNumber: expense.cardNumber || '',
            expenseDate: expense.expenseDate,
            vendor: expense.vendor,
            categoryId: String(expense.categoryId),
            jobNumber: expense.jobNumber || '',
            plateNumber: expense.plateNumber || '',
            amountPaid: String(expense.amountPaid),
            currency: expense.currency || 'CAD',
            originalAmount: expense.originalAmount != null ? String(expense.originalAmount) : '',
            cadConfirmed: expense.currency && expense.currency !== 'CAD' ? !expense.cadEstimated : false,
            tipAmount: expense.tipAmount > 0 ? String(expense.tipAmount) : '',
            carWash: expense.carWashAmount > 0,
            carWashAmount: expense.carWashAmount > 0 ? String(expense.carWashAmount) : '',
            hasTax: expense.hasTax,
            notes: expense.notes || '',
        })
    }

    function cancelEdit() {
        setEditing(null)
        setDraftReceipt(null)
    }

    /**
     * Attaches a picked image to a line that is already saved.
     *
     * Returns null, or the reason it did not work. The line itself is stored by
     * the time this runs, so a failure here is said plainly rather than reported
     * as if the whole correction had been lost.
     */
    async function attachReceipt(uuid, picked) {
        try {
            const response = await expenseApi.saveReceipt(uuid, picked.dataUrl)
            if (response.success) return null
            return response.message || 'The line was saved, but the receipt could not be attached.'
        } catch {
            return 'The line was saved, but the receipt could not be attached.'
        }
    }

    async function saveEdit(uuid) {
        const badAmount = amountProblem(draft)
        if (badAmount) {
            toast.error(badAmount)
            return
        }
        const amount = Number(draft.amountPaid)
        if (!draft.vendor.trim()) {
            toast.error('Vendor is required')
            return
        }
        if (!draft.cardNumber.trim()) {
            toast.error('Card number is required')
            return
        }

        const tip = tipFor(draft, amount)
        if (tip === null) return

        const wash = carWashFor(draft, amount, tip)
        if (wash === null) return

        const plate = plateFor(draft)
        if (plate === null) return

        // The list is filtered by card, so correcting the card number moves the
        // line out of the view you are looking at. That is right - it belongs to
        // a different report now - but it needs saying, or it reads as a delete.
        const movedCard = draft.cardNumber.trim() !== selected.cardNumber

        try {
            const response = await expenseApi.update(uuid, {
                cardNumber: draft.cardNumber.trim(),
                expenseDate: draft.expenseDate,
                vendor: draft.vendor.trim(),
                categoryId: Number(draft.categoryId),
                // Empty string clears it; the server treats blank as no job. Both
                // are always sent, so recategorising a line drops whichever no
                // longer applies rather than leaving it stranded on the row.
                jobNumber: usesPlate(draft.categoryId) ? '' : draft.jobNumber.trim(),
                plateNumber: plate,
                ...amountFields(draft),
                tipAmount: tip,
                carWashAmount: wash,
                notes: draft.notes.trim(),
            })

            if (response.success) {
                // After the update, not before: the file is named from the date,
                // vendor and amount on the row, so it should be named from the
                // corrected ones. Replacing deletes the old image server-side.
                const receiptProblem = draftReceipt ? await attachReceipt(uuid, draftReceipt) : null

                cancelEdit()
                if (movedCard) {
                    toast.success(`Moved to card ending ${draft.cardNumber.trim()} — it now appears under that card.`)
                    loadEmployees()   // the new card may not have had a group before
                } else {
                    toast.success(draftReceipt && !receiptProblem ? 'Updated, receipt attached' : 'Updated')
                }
                if (receiptProblem) toast.error(receiptProblem)
                loadRows()
            } else {
                toast.error(response.message || 'Could not update')
            }
        } catch {
            toast.error('Could not update')
        }
    }

    function startAdd() {
        setNewLine({
            cardNumber: selected.cardNumber || '',
            expenseDate: `${month}-01`,
            vendor: '',
            categoryId: categories[0] ? String(categories[0].id) : '',
            jobNumber: '',
            plateNumber: '',
            amountPaid: '',
            currency: 'CAD',
            originalAmount: '',
            cadConfirmed: false,
            tipAmount: '',
            carWash: false,
            carWashAmount: '',
            hasTax: true,
            notes: '',
        })
        setNewReceipt(null)
        setAdding(true)
    }

    function cancelAdd() {
        setAdding(false)
        setNewLine(null)
        setNewReceipt(null)
    }

    // Filed against the selected employee and card, not against the admin doing
    // the typing. The server records who actually entered it.
    async function saveNew() {
        const badAmount = amountProblem(newLine)
        if (badAmount) {
            toast.error(badAmount)
            return
        }
        const amount = Number(newLine.amountPaid)
        if (!newLine.vendor.trim()) {
            toast.error('Vendor is required')
            return
        }
        if (!newLine.categoryId) {
            toast.error('Category is required')
            return
        }

        const tip = tipFor(newLine, amount)
        if (tip === null) return

        const wash = carWashFor(newLine, amount, tip)
        if (wash === null) return

        const plate = plateFor(newLine)
        if (plate === null) return

        try {
            const response = await expenseApi.create({
                employeeUser: selected.employeeUser,
                cardNumber: (newLine.cardNumber || selected.cardNumber).trim(),
                expenseDate: newLine.expenseDate,
                vendor: newLine.vendor.trim(),
                categoryId: Number(newLine.categoryId),
                jobNumber: usesPlate(newLine.categoryId) ? null : newLine.jobNumber.trim() || null,
                plateNumber: plate || null,
                ...amountFields(newLine),
                tipAmount: tip,
                carWashAmount: wash,
                notes: newLine.notes.trim() || null,
                // Create writes the image itself, so a line added from a paper
                // receipt at month end is filed with it in one go.
                receiptDataUrl: newReceipt?.dataUrl || null,
            })

            if (response.success) {
                toast.success(newReceipt ? 'Line added with its receipt' : 'Line added')
                cancelAdd()
                loadRows()
                loadEmployees()
            } else {
                toast.error(response.message || 'Could not add the line')
            }
        } catch {
            toast.error('Could not add the line')
        }
    }

    const estimatedCount = useMemo(() => expenses.filter(e => e.cadEstimated).length, [expenses])

    /**
     * Accepts a foreign line's estimate as the statement figure. Only for when it
     * really matches - usually it will not, since cards add a fee, and then the
     * figure is corrected through Edit instead.
     */
    async function confirmCad(expense) {
        if (!confirm(`Does the card statement show exactly ${money(expense.amountPaid)} for ${expense.vendor} (${expense.currency} ${Number(expense.originalAmount).toFixed(2)})?\n\nIf it shows a different figure, use Edit and enter that instead.`)) return

        try {
            const response = await expenseApi.update(expense.uuid, {
                amountPaid: expense.amountPaid,
                cadConfirmed: true,
            })
            if (response.success) {
                toast.success('CAD confirmed')
                loadRows()
            } else {
                toast.error(response.message || 'Could not confirm')
            }
        } catch {
            toast.error('Could not confirm')
        }
    }

    async function remove(expense) {
        if (!confirm(`Delete the ${money(expense.amountPaid)} expense from ${expense.vendor} on ${expense.expenseDate}?\n\nAny receipt image is deleted with it.`)) return

        try {
            const response = await expenseApi.delete(expense.uuid)
            if (response.success) {
                toast.success('Deleted')
                loadRows()
            } else {
                toast.error(response.message || 'Could not delete')
            }
        } catch {
            toast.error('Could not delete')
        }
    }

    async function generate() {
        if (!selected) return

        setGenerating(true)
        try {
            const { blob, filename } = await expenseApi.downloadReport({
                employeeUser: selected.employeeUser,
                cardNumber: selected.cardNumber,
                startDate: `${month}-01`,
                endDate: monthEnd(month),
            })

            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)

            toast.success('Report generated')
        } catch (error) {
            toast.error(error.message || 'Could not generate the report')
        }
        setGenerating(false)
    }

    return (
        <div>
            <div className="page-header flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="page-title">Expense Reports</h1>
                    <p className="text-blue-100 mt-1">Review and correct a month, then generate the PDF.</p>
                </div>
                <Link to="/admin/expense-categories" className="btn btn-secondary">
                    Categories
                </Link>
            </div>

            <div className="card mb-6">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="form-group mb-0">
                        <label className="form-label">Month</label>
                        <input
                            type="month"
                            value={month}
                            onChange={e => setMonth(e.target.value)}
                            style={{ width: 'auto' }}
                        />
                    </div>
                    <p className="text-sm text-gray-500 pb-2">
                        {loadingEmployees
                            ? 'Loading…'
                            : `${employees.length} employee/card combination${employees.length === 1 ? '' : 's'} with expenses in ${monthLabel(month)}`}
                    </p>
                </div>
            </div>

            {/* ---- Who ---- */}
            {loadingEmployees ? (
                <div className="flex justify-center py-8"><div className="spinner w-8 h-8" /></div>
            ) : employees.length === 0 ? (
                <div className="card text-center text-gray-500 py-10">
                    No expenses were entered in {monthLabel(month)}.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {employees.map(emp => {
                        const isSelected = selected?.employeeUser === emp.employeeUser
                            && selected?.cardNumber === emp.cardNumber
                        return (
                            <button
                                key={`${emp.employeeUser}-${emp.cardNumber}`}
                                onClick={() => setSelected(emp)}
                                className={`text-left p-4 rounded-xl shadow-md transition-all ${
                                    isSelected ? 'bg-rdi-blue text-white' : 'bg-white hover:shadow-lg'
                                }`}
                            >
                                <p className="font-semibold">{emp.fullName || emp.employeeUser}</p>
                                <p className={`text-sm ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>
                                    Card ending {emp.cardNumber} · {emp.lineCount} line{emp.lineCount === 1 ? '' : 's'}
                                </p>
                                <p className={`mt-1 font-bold ${isSelected ? 'text-white' : 'text-rdi-blue'}`}>
                                    {money(emp.total)}
                                </p>
                            </button>
                        )
                    })}
                </div>
            )}

            {/* ---- The month ---- */}
            {selected && (
                <div className="card">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <h2 className="text-lg font-semibold text-gray-700">
                            {selected.fullName || selected.employeeUser}
                            <span className="text-gray-400 font-normal ml-2">
                                card ending {selected.cardNumber} · {monthLabel(month)}
                            </span>
                        </h2>
                        <div className="flex gap-2">
                            <button
                                className="btn btn-secondary"
                                onClick={startAdd}
                                disabled={adding}
                            >
                                Add Line
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={generate}
                                disabled={generating || expenses.length === 0}
                            >
                                {generating ? 'Generating…' : 'Generate PDF'}
                            </button>
                        </div>
                    </div>

                    {loadingRows ? (
                        <div className="flex justify-center py-8"><div className="spinner w-8 h-8" /></div>
                    ) : expenses.length === 0 && !adding ? (
                        <p className="text-gray-500 text-center py-8">Nothing on this card for this month.</p>
                    ) : (
                        <>
                        {/* Estimates are the one thing on this screen that is known to
                            be wrong, so they are called out before the PDF goes out. */}
                        {estimatedCount > 0 && (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                                <strong>{estimatedCount} foreign line{estimatedCount === 1 ? '' : 's'}</strong>{' '}
                                still {estimatedCount === 1 ? 'uses an' : 'use'} estimated CAD amount{estimatedCount === 1 ? '' : 's'}.
                                {' '}Check {estimatedCount === 1 ? 'it' : 'each one'} against the card statement: Edit to enter
                                the statement figure, or Confirm if the estimate already matches.
                            </div>
                        )}

                        {/* Phones: one card per line. Nine columns cannot fit on a
                            phone, and a sideways-scrolling table is unusable when
                            the amounts are the point. */}
                        <ul className="md:hidden divide-y">
                            {adding && newLine && (
                                <li className="py-4 bg-green-50 -mx-6 px-6">
                                    <p className="font-semibold text-gray-800 mb-3">New line</p>
                                    <LineFields
                                        value={newLine}
                                        onChange={setNewLine}
                                        categories={categories}
                                        taxRate={taxRate}
                                        currencies={currencies}
                                        receipt={newReceipt}
                                        onReceiptChange={setNewReceipt}
                                    />
                                    <div className="flex gap-2 mt-4">
                                        <button className="btn btn-primary flex-1" onClick={saveNew}>Add</button>
                                        <button
                                            className="btn btn-secondary flex-1"
                                            onClick={cancelAdd}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </li>
                            )}

                            {expenses.map(e => (
                                <li key={e.uuid} className={`py-4 ${editing === e.uuid ? 'bg-blue-50 -mx-6 px-6' : ''}`}>
                                    {editing === e.uuid ? (
                                        <>
                                            <p className="font-semibold text-gray-800 mb-3">Editing</p>
                                            <LineFields
                                                value={draft}
                                                onChange={setDraft}
                                                categories={categories}
                                                taxRate={taxRate}
                                                currencies={currencies}
                                                receipt={draftReceipt}
                                                onReceiptChange={setDraftReceipt}
                                                hasReceipt={e.hasReceipt}
                                                onViewReceipt={() => openReceipt(e)}
                                                viewingReceipt={openingReceipt === e.uuid}
                                            />
                                            <div className="flex gap-2 mt-4">
                                                <button className="btn btn-primary flex-1" onClick={() => saveEdit(e.uuid)}>Save</button>
                                                <button className="btn btn-secondary flex-1" onClick={cancelEdit}>Cancel</button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-semibold text-gray-900 break-words">{e.vendor}</p>
                                                    <p className="text-sm text-gray-500">
                                                        {e.expenseDate} · {e.categoryName}
                                                    </p>
                                                    {(e.jobNumber || e.plateNumber || e.notes) && (
                                                        <p className="text-sm text-gray-500 break-words mt-0.5">
                                                            {[e.jobNumber, e.plateNumber, e.notes].filter(Boolean).join(' — ')}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <p className="font-semibold text-gray-900 whitespace-nowrap">
                                                        {money(e.amountPaid)}
                                                    </p>
                                                    <ForeignAmountNote expense={e} className="block" />
                                                    {e.cadEstimated && (
                                                        <button
                                                            className="text-rdi-blue text-xs font-medium hover:underline"
                                                            onClick={() => confirmCad(e)}
                                                        >
                                                            Confirm CAD
                                                        </button>
                                                    )}
                                                    <p className="text-xs text-gray-500 whitespace-nowrap">
                                                        {money(e.preTax)} + {money(e.tax)} tax
                                                        {e.tipAmount > 0 && ` + ${money(e.tipAmount)} tip`}
                                                    </p>
                                                    {e.carWashAmount > 0 && (
                                                        <p className="text-xs text-gray-500 whitespace-nowrap">
                                                            incl. {money(e.carWashPaid)} car wash
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4 mt-3">
                                                {e.hasReceipt ? (
                                                    <button
                                                        onClick={() => openReceipt(e)}
                                                        disabled={openingReceipt === e.uuid}
                                                        className="text-rdi-blue text-sm hover:underline disabled:opacity-50"
                                                    >
                                                        {openingReceipt === e.uuid ? "Opening…" : "📎 Receipt"}
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-400 text-sm">No receipt</span>
                                                )}
                                                <span className="flex-1" />
                                                <button className="text-rdi-blue text-sm font-medium" onClick={() => startEdit(e)}>
                                                    Edit
                                                </button>
                                                <button className="text-rdi-red text-sm font-medium" onClick={() => remove(e)}>
                                                    Delete
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {/* Phone totals, since there is no table footer to carry them */}
                        <div className="md:hidden mt-4 pt-4 border-t-2 border-gray-300 space-y-1 text-sm">
                            <div className="flex justify-between text-gray-600">
                                <span>{expenses.length} line{expenses.length === 1 ? '' : 's'} · pre-tax</span>
                                <span>{money(totals.preTax)}</span>
                            </div>
                            <div className="flex justify-between text-gray-600">
                                <span>Tax</span>
                                <span>{money(totals.tax)}</span>
                            </div>
                            {totals.tip > 0 && (
                                <div className="flex justify-between text-gray-600">
                                    <span>Tips</span>
                                    <span>{money(totals.tip)}</span>
                                </div>
                            )}
                            <div className="flex justify-between font-semibold text-rdi-blue text-base">
                                <span>Total charged</span>
                                <span>{money(totals.paid)}</span>
                            </div>
                        </div>

                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-gray-500 border-b">
                                        <th className="py-2">Date</th>
                                        <th className="py-2">Vendor</th>
                                        <th className="py-2">Category</th>
                                        <th className="py-2">Job / Plate / Note</th>
                                        <th className="py-2 text-center">Rcpt</th>
                                        <th className="py-2 text-right">Pre-tax</th>
                                        <th className="py-2 text-right">Tax</th>
                                        <th className="py-2 text-right">Tip</th>
                                        <th className="py-2 text-right">Paid</th>
                                        <th className="py-2" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {adding && newLine && (
                                        <tr className="border-b border-gray-100 bg-green-50">
                                            <td className="py-2 pr-2">
                                                <input
                                                    type="date"
                                                    value={newLine.expenseDate}
                                                    onChange={ev => setNewLine({ ...newLine, expenseDate: ev.target.value })}
                                                />
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={10}
                                                    className="mt-1"
                                                    value={newLine.cardNumber || ''}
                                                    onChange={ev => setNewLine({ ...newLine, cardNumber: ev.target.value })}
                                                    placeholder="Card ending"
                                                    title="Card number"
                                                />
                                            </td>
                                            <td className="py-2 pr-2">
                                                <input
                                                    type="text"
                                                    autoFocus
                                                    value={newLine.vendor}
                                                    onChange={ev => setNewLine({ ...newLine, vendor: ev.target.value })}
                                                    placeholder="Vendor"
                                                />
                                            </td>
                                            <td className="py-2 pr-2">
                                                <select
                                                    value={newLine.categoryId}
                                                    onChange={ev => setNewLine({ ...newLine, categoryId: ev.target.value })}
                                                >
                                                    <option value="">Category…</option>
                                                    {categories.filter(c => c.isActive).map(c => (
                                                        <option key={c.id} value={c.id}>{c.displayName}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="py-2 pr-2">
                                                {usesPlate(newLine.categoryId) ? (
                                                    <PlateField
                                                        value={newLine.plateNumber}
                                                        onChange={v => setNewLine({ ...newLine, plateNumber: v })}
                                                        placeholder="Plate…"
                                                    />
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={newLine.jobNumber}
                                                        onChange={ev => setNewLine({ ...newLine, jobNumber: ev.target.value })}
                                                        placeholder="Job number"
                                                    />
                                                )}
                                                <input
                                                    type="text"
                                                    className="mt-1"
                                                    value={newLine.notes}
                                                    onChange={ev => setNewLine({ ...newLine, notes: ev.target.value })}
                                                    placeholder="Note"
                                                />
                                            </td>
                                            <td className="py-2 pr-2">
                                                <ReceiptPicker
                                                    value={newReceipt}
                                                    onChange={setNewReceipt}
                                                    compact
                                                />
                                            </td>
                                            {/* Pre-tax and tax are worked out on save, so the two
                                                cells they will fill are free while the line is being
                                                typed - HST sits there, under its own totals. */}
                                            <td className="py-2 pr-2" colSpan={2}>
                                                <InlineCurrency line={newLine} onChange={setNewLine} currencies={currencies} />
                                            </td>
                                            <td className="py-2 pr-2">
                                                {promptsTip(newLine.categoryId) && !isForeign(newLine) && (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={newLine.tipAmount}
                                                        onChange={ev => setNewLine({ ...newLine, tipAmount: ev.target.value })}
                                                        placeholder="Tip"
                                                        title="Tip included in the amount paid"
                                                    />
                                                )}
                                                {/* Fuel takes no tip, so the wash borrows this cell. */}
                                                {washTarget(newLine.categoryId) != null && !isForeign(newLine) && (
                                                    <InlineCarWash line={newLine} onChange={setNewLine} filedUnder={washTarget(newLine.categoryId)} />
                                                )}
                                            </td>
                                            <td className="py-2 pr-2">
                                                <InlinePaid line={newLine} onChange={setNewLine} />
                                            </td>
                                            <td className="py-2 whitespace-nowrap text-right">
                                                <button
                                                    className="text-rdi-blue font-medium hover:underline mr-2"
                                                    onClick={saveNew}
                                                >
                                                    Add
                                                </button>
                                                <button
                                                    className="text-gray-500 hover:underline"
                                                    onClick={cancelAdd}
                                                >
                                                    Cancel
                                                </button>
                                            </td>
                                        </tr>
                                    )}

                                    {expenses.map(e => (
                                        editing === e.uuid ? (
                                            <tr key={e.uuid} className="border-b border-gray-100 bg-blue-50">
                                                {/* Card number stacks under the date rather than
                                                    taking a tenth column - the table is already wide. */}
                                                <td className="py-2 pr-2">
                                                    <input
                                                        type="date"
                                                        value={draft.expenseDate}
                                                        onChange={ev => setDraft({ ...draft, expenseDate: ev.target.value })}
                                                    />
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        maxLength={10}
                                                        className="mt-1"
                                                        value={draft.cardNumber || ''}
                                                        onChange={ev => setDraft({ ...draft, cardNumber: ev.target.value })}
                                                        placeholder="Card ending"
                                                        title="Card number"
                                                    />
                                                </td>
                                                <td className="py-2 pr-2">
                                                    <input
                                                        type="text"
                                                        value={draft.vendor}
                                                        onChange={ev => setDraft({ ...draft, vendor: ev.target.value })}
                                                    />
                                                </td>
                                                <td className="py-2 pr-2">
                                                    <select
                                                        value={draft.categoryId}
                                                        onChange={ev => setDraft({ ...draft, categoryId: ev.target.value })}
                                                    >
                                                        {categories.map(c => (
                                                            <option key={c.id} value={c.id}>{c.displayName}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="py-2 pr-2">
                                                    {usesPlate(draft.categoryId) ? (
                                                        <PlateField
                                                            value={draft.plateNumber}
                                                            onChange={v => setDraft({ ...draft, plateNumber: v })}
                                                            placeholder="Plate…"
                                                        />
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={draft.jobNumber}
                                                            onChange={ev => setDraft({ ...draft, jobNumber: ev.target.value })}
                                                            placeholder="Job number"
                                                        />
                                                    )}
                                                    <input
                                                        type="text"
                                                        className="mt-1"
                                                        value={draft.notes}
                                                        onChange={ev => setDraft({ ...draft, notes: ev.target.value })}
                                                        placeholder="Note"
                                                    />
                                                </td>
                                                <td className="py-2 pr-2">
                                                    <ReceiptPicker
                                                        value={draftReceipt}
                                                        onChange={setDraftReceipt}
                                                        hasExisting={e.hasReceipt}
                                                        onViewExisting={() => openReceipt(e)}
                                                        viewingExisting={openingReceipt === e.uuid}
                                                        compact
                                                    />
                                                </td>
                                                <td className="py-2 pr-2" colSpan={2}>
                                                    <InlineCurrency line={draft} onChange={setDraft} currencies={currencies} />
                                                </td>
                                                <td className="py-2 pr-2">
                                                    {promptsTip(draft.categoryId) && !isForeign(draft) && (
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={draft.tipAmount}
                                                            onChange={ev => setDraft({ ...draft, tipAmount: ev.target.value })}
                                                            placeholder="Tip"
                                                            title="Tip included in the amount paid"
                                                        />
                                                    )}
                                                    {washTarget(draft.categoryId) != null && !isForeign(draft) && (
                                                        <InlineCarWash line={draft} onChange={setDraft} filedUnder={washTarget(draft.categoryId)} />
                                                    )}
                                                </td>
                                                <td className="py-2 pr-2">
                                                    <InlinePaid line={draft} onChange={setDraft} />
                                                </td>
                                                <td className="py-2 whitespace-nowrap">
                                                    <button
                                                        className="text-rdi-blue font-medium hover:underline mr-2"
                                                        onClick={() => saveEdit(e.uuid)}
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        className="text-gray-500 hover:underline"
                                                        onClick={cancelEdit}
                                                    >
                                                        Cancel
                                                    </button>
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr key={e.uuid} className="border-b border-gray-100">
                                                <td className="py-2 whitespace-nowrap">{e.expenseDate}</td>
                                                <td className="py-2">{e.vendor}</td>
                                                <td className="py-2 text-gray-600">{e.categoryName}</td>
                                                <td className="py-2 text-gray-600">
                                                    {[e.jobNumber, e.plateNumber, e.notes].filter(Boolean).join(' — ')}
                                                    {e.carWashAmount > 0 && (
                                                        <span className="block text-xs text-gray-500">
                                                            incl. {money(e.carWashPaid)} car wash
                                                            {e.carWashCategoryName && ` → ${e.carWashCategoryName}`}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-2 text-center">
                                                    {e.hasReceipt ? (
                                                        <button
                                                            onClick={() => openReceipt(e)}
                                                            disabled={openingReceipt === e.uuid}
                                                            className="text-rdi-blue hover:underline disabled:opacity-50"
                                                            title="View receipt"
                                                        >
                                                            {openingReceipt === e.uuid ? "…" : "📎"}
                                                        </button>
                                                    ) : (
                                                        <span className="text-gray-300" title="No receipt">—</span>
                                                    )}
                                                </td>
                                                <td className="py-2 text-right whitespace-nowrap">{money(e.preTax)}</td>
                                                <td className="py-2 text-right whitespace-nowrap text-gray-500">{money(e.tax)}</td>
                                                <td className="py-2 text-right whitespace-nowrap text-gray-500">
                                                    {e.tipAmount > 0 ? money(e.tipAmount) : '—'}
                                                </td>
                                                <td className="py-2 text-right whitespace-nowrap font-medium">
                                                    {money(e.amountPaid)}
                                                    <ForeignAmountNote expense={e} className="block font-normal" />
                                                </td>
                                                <td className="py-2 whitespace-nowrap text-right">
                                                    {e.cadEstimated && (
                                                        <button
                                                            className="text-rdi-blue font-medium hover:underline mr-2"
                                                            onClick={() => confirmCad(e)}
                                                            title="The estimate matches the card statement"
                                                        >
                                                            Confirm
                                                        </button>
                                                    )}
                                                    <button
                                                        className="text-rdi-blue hover:underline mr-2"
                                                        onClick={() => startEdit(e)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="text-rdi-red hover:underline"
                                                        onClick={() => remove(e)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="font-semibold border-t-2 border-gray-300">
                                        <td className="py-3" colSpan={5}>
                                            {expenses.length} line{expenses.length === 1 ? '' : 's'}
                                        </td>
                                        <td className="py-3 text-right whitespace-nowrap">{money(totals.preTax)}</td>
                                        <td className="py-3 text-right whitespace-nowrap">{money(totals.tax)}</td>
                                        <td className="py-3 text-right whitespace-nowrap">{money(totals.tip)}</td>
                                        <td className="py-3 text-right whitespace-nowrap text-rdi-blue">{money(totals.paid)}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <p className="text-xs text-gray-500 mt-4">
                            Paid is what went on the card. Pre-tax, tax and tip are worked out from it, so
                            every line adds up exactly and the total matches the statement. A tip is never
                            taxed, so it comes off before the tax is split out.
                        </p>
                        </>
                    )}
                </div>
            )}

            <ReceiptViewer receipt={receiptView} onClose={closeReceipt} />
        </div>
    )
}

/**
 * Currency and HST for the desktop table's inline row, in the two cells pre-tax
 * and tax will fill. HST goes away for a foreign receipt - none is claimable.
 */
function InlineCurrency({ line, onChange, currencies }) {
    const foreign = isForeign(line)

    function setCurrency(currency) {
        // As on the stacked form: the number in the Paid box keeps its place.
        const typed = foreign ? line.originalAmount : line.amountPaid
        onChange(currency === 'CAD'
            ? { ...line, currency, amountPaid: typed, originalAmount: '', cadConfirmed: false, hasTax: true }
            : { ...line, currency, originalAmount: typed, amountPaid: '', cadConfirmed: false, hasTax: false })
    }

    return (
        <div className="flex items-center gap-2">
            <select
                value={line.currency || 'CAD'}
                onChange={ev => setCurrency(ev.target.value)}
                style={{ width: 'auto' }}
                title="The currency the receipt is printed in"
            >
                <option value="CAD">CAD</option>
                {currencies.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {!foreign && (
                <label className="text-xs text-gray-600 whitespace-nowrap">
                    <input
                        type="checkbox"
                        style={{ width: 'auto' }}
                        checked={line.hasTax}
                        onChange={ev => onChange({ ...line, hasTax: ev.target.checked })}
                    />
                    <span className="ml-1">HST</span>
                </label>
            )}
        </div>
    )
}

/**
 * The Paid cell. A foreign line takes the receipt's own figure, plus the CAD
 * from the statement when it is known - typing one confirms it; leaving it
 * blank has the server estimate it.
 */
function InlinePaid({ line, onChange }) {
    if (!isForeign(line)) {
        return (
            <input
                type="number"
                step="0.01"
                min="0"
                value={line.amountPaid}
                onChange={ev => onChange({ ...line, amountPaid: ev.target.value })}
                placeholder="0.00"
            />
        )
    }

    return (
        <>
            <input
                type="number"
                step="0.01"
                min="0"
                value={line.originalAmount}
                onChange={ev => onChange({ ...line, originalAmount: ev.target.value })}
                placeholder={line.currency}
                title={`Total as printed on the receipt, in ${line.currency}`}
            />
            <input
                type="number"
                step="0.01"
                min="0"
                className="mt-1"
                value={line.cadConfirmed ? line.amountPaid : ''}
                onChange={ev => onChange({
                    ...line,
                    amountPaid: ev.target.value,
                    cadConfirmed: ev.target.value.trim() !== '',
                })}
                placeholder="CAD (statement)"
                title="CAD from the card statement. Leave blank to use the Bank of Canada estimate."
            />
        </>
    )
}

/**
 * The car wash box for the desktop table's inline row. A blank box is no wash,
 * so it needs no separate tick - the phone form has the checkbox and breakdown.
 */
function InlineCarWash({ line, onChange, filedUnder }) {
    return (
        <input
            type="number"
            step="0.01"
            min="0"
            value={line.carWashAmount || ''}
            onChange={ev => onChange({
                ...line,
                carWashAmount: ev.target.value,
                carWash: ev.target.value.trim() !== '',
            })}
            placeholder="Wash"
            title={`Car wash price before tax, if one is on this receipt. Filed under ${filedUnder}.`}
        />
    )
}

/**
 * The line's fields, stacked and labelled.
 *
 * Used on phones for both adding and editing, where the inline table row has
 * nowhere to put nine columns. The desktop table keeps its own inline inputs -
 * editing in place is the point of it.
 */
function LineFields({
    value,
    onChange,
    categories,
    receipt,
    onReceiptChange,
    hasReceipt = false,
    onViewReceipt,
    viewingReceipt = false,
    taxRate,
    currencies,
}) {
    const set = (key, v) => onChange({ ...value, [key]: v })

    const category = categories.find(c => String(c.id) === String(value.categoryId))
    const tipCategory = category?.promptsTip === true && !isForeign(value)
    const plateCategory = category?.usesPlate === true
    const washCategory = category?.carWashCategoryId != null && !isForeign(value)

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="form-label">Card ending in</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        maxLength={10}
                        value={value.cardNumber || ""}
                        onChange={e => set("cardNumber", e.target.value)}
                        placeholder="1234"
                    />
                </div>
                <div>
                    <label className="form-label">Date</label>
                    <input
                        type="date"
                        value={value.expenseDate}
                        onChange={e => set('expenseDate', e.target.value)}
                    />
                </div>
            </div>

            <AmountField value={value} onChange={onChange} currencies={currencies} help={false} required={false} />

            <div>
                <label className="form-label">Vendor</label>
                <input
                    type="text"
                    value={value.vendor}
                    onChange={e => set('vendor', e.target.value)}
                    placeholder="Vendor"
                />
            </div>

            <div>
                <label className="form-label">Category</label>
                <select value={value.categoryId} onChange={e => set('categoryId', e.target.value)}>
                    <option value="">Category…</option>
                    {categories.filter(c => c.isActive || String(c.id) === String(value.categoryId))
                        .map(c => <option key={c.id} value={c.id}>{c.displayName}</option>)}
                </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="form-label">{plateCategory ? 'Plate number' : 'Job number'}</label>
                    {plateCategory ? (
                        <PlateField
                            value={value.plateNumber || ''}
                            onChange={v => set('plateNumber', v)}
                        />
                    ) : (
                        <input
                            type="text"
                            value={value.jobNumber}
                            onChange={e => set('jobNumber', e.target.value)}
                            placeholder="Optional"
                        />
                    )}
                </div>
                <div>
                    <label className="form-label">Note</label>
                    <input
                        type="text"
                        value={value.notes}
                        onChange={e => set('notes', e.target.value)}
                        placeholder="Optional"
                    />
                </div>
            </div>

            {/* Only for a category that takes tips, so the box cannot be used to
                put a tip on a fuel receipt. */}
            {tipCategory && (
                <div>
                    <label className="form-label">Tip included</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        inputMode="decimal"
                        value={value.tipAmount || ''}
                        onChange={e => set('tipAmount', e.target.value)}
                        placeholder="0.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Part of the amount paid, not on top of it. No tax is worked out on it.
                    </p>
                </div>
            )}


            {washCategory && (
                <CarWashField
                    value={value}
                    onChange={onChange}
                    taxRate={taxRate}
                    filedUnder={categories.find(c => c.id === category.carWashCategoryId)?.name || 'its own category'}
                    mainLabel={category.name}
                />
            )}

            {/* The phone is where a receipt actually gets photographed, so the
                camera sits on the form rather than behind another tap. */}
            <div>
                <label className="form-label">Receipt</label>
                <ReceiptPicker
                    value={receipt}
                    onChange={onReceiptChange}
                    hasExisting={hasReceipt}
                    onViewExisting={onViewReceipt}
                    viewingExisting={viewingReceipt}
                />
            </div>
        </div>
    )
}
